import React, { useState, useEffect, useRef } from 'react';
import { X } from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { athleteAPI, userAPI } from '../services/api';
import { useResponsive } from '../hooks/useResponsive';
import { birthdateError } from '../utils/validation';
import Modal from './Modal';
import { Select, TextInput } from './ui';
import { normalizeSocialHandle } from '../utils/socialHandles';
import { checkVideoLink, VIDEO_ERROR_HUDL, VIDEO_ERROR_OTHER } from '../utils/youtube';

const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

interface PersonalInfoEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData: any;
  onUpdate: (data: any) => void;
  /** Render inside the unified ProfileEditModal (no own overlay). */
  embedded?: boolean;
  /** Called after a successful save. Falls back to onClose. */
  onSaved?: () => void;
}

const PersonalInfoEditModal: React.FC<PersonalInfoEditModalProps> = ({
  isOpen,
  onClose,
  initialData,
  onUpdate,
  embedded = false,
  onSaved,
}) => {
  const { sessionToken, user, setUser } = useAuth();
  const toast = useToast();
  const { isMobile } = useResponsive();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    graduationYear: '',
    city: '',
    state: '',
    ncaaId: '',
    xHandle: '',
    instagramHandle: '',
    featuredVideoUrl: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);
  // The handles as the form opened with them, normalised. See the save below.
  const openedHandles = useRef({ x: '', instagram: '' });
  // APS-259. The video link as the form opened with it. Sent only when changed.
  const openedVideo = useRef('');

  // Helper function to format date for input (parse YYYY-MM-DD directly to avoid timezone shifts)
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    try {
      // If already in YYYY-MM-DD format, return as-is
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
      const date = new Date(dateString);
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Load initial data when modal opens
  useEffect(() => {
    if (isOpen && initialData) {
      // Format date of birth for date input (YYYY-MM-DD)
      let formattedDate = '';
      if (initialData.date_of_birth) {
        // Parse YYYY-MM-DD directly to avoid timezone offset shifting the day
        const dobStr = initialData.date_of_birth;
        if (/^\d{4}-\d{2}-\d{2}/.test(dobStr)) {
          formattedDate = dobStr.substring(0, 10);
        } else {
          const date = new Date(dobStr);
          const year = date.getUTCFullYear();
          const month = String(date.getUTCMonth() + 1).padStart(2, '0');
          const day = String(date.getUTCDate()).padStart(2, '0');
          formattedDate = `${year}-${month}-${day}`;
        }
      }

      setFormData({
        firstName: initialData.first_name || '',
        lastName: initialData.last_name || '',
        email: initialData.email || '',
        phone: initialData.phone_number || '',
        dateOfBirth: formattedDate,
        graduationYear: initialData.graduation_year?.toString() || '',
        city: initialData.city || '',
        state: initialData.state || '',
        ncaaId: initialData.ncaa_id || '',
        xHandle: initialData.x_handle || '',
        instagramHandle: initialData.instagram_handle || '',
        featuredVideoUrl: initialData.featured_video_url || '',
      });
      openedVideo.current = initialData.featured_video_url || '';
      openedHandles.current = {
        x: normalizeSocialHandle(initialData.x_handle, 'x').handle || '',
        instagram: normalizeSocialHandle(initialData.instagram_handle, 'instagram').handle || '',
      };
      setErrors({});
      setHasChanges(false);
    }
  }, [isOpen, initialData]);

  const cleanPhoneNumber = (phone: string): string => {
    return phone.replace(/\D/g, '');
  };

  const formatPhoneNumber = (phone: string): string => {
    const cleaned = cleanPhoneNumber(phone);
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
    if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  // Static range matched to the backend CHECK (check_graduation_year_valid: 2025-2035).
  // A currentYear-relative range drifts out of sync and offers years the backend rejects
  // (plan 0.1 — a measured rage-click cause on the profile page).
  const GRAD_YEAR_MIN = 2025;
  const GRAD_YEAR_MAX = 2035;
  const generateGraduationYears = (): number[] => {
    const years: number[] = [];
    for (let year = GRAD_YEAR_MIN; year <= GRAD_YEAR_MAX; year++) years.push(year);
    // Inject the athlete's stored year if it's outside the range so a legacy value never
    // renders blank (they get a clear inline prompt to correct it on save, not a mystery toast).
    const stored = parseInt(formData.graduationYear, 10);
    if (stored && !years.includes(stored)) {
      years.push(stored);
      years.sort((a, b) => a - b);
    }
    return years;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.phone) {
      const cleaned = cleanPhoneNumber(formData.phone);
      if (cleaned.length !== 0 && (cleaned.length !== 10 || !/^\d+$/.test(cleaned))) {
        newErrors.phone = 'Please enter a valid 10-digit phone number';
      }
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else {
      // #189 — reject future / implausible birthdates.
      const dobErr = birthdateError(formData.dateOfBirth);
      if (dobErr) newErrors.dateOfBirth = dobErr;
    }

    if (!formData.graduationYear) {
      newErrors.graduationYear = 'Graduation year is required';
    } else {
      const gy = parseInt(formData.graduationYear, 10);
      if (gy < GRAD_YEAR_MIN || gy > GRAD_YEAR_MAX) {
        newErrors.graduationYear = `Choose a graduation year between ${GRAD_YEAR_MIN} and ${GRAD_YEAR_MAX}.`;
      }
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    } else if (formData.city.trim().length < 2) {
      newErrors.city = 'City must be at least 2 characters';
    }

    if (!formData.state) {
      newErrors.state = 'State is required';
    }

    /* APS-255. Both optional, and both CLEARABLE: an empty box means "remove
       it", which is why the payload below sends '' rather than dropping the
       key. The server re-runs this exact check (backend utils/socialHandles.js)
       and the database has a CHECK behind that — this copy exists so the
       message lands under the input instead of arriving as a toast. */
    const xCheck = normalizeSocialHandle(formData.xHandle, 'x');
    if (xCheck.error) newErrors.xHandle = xCheck.error;
    const igCheck = normalizeSocialHandle(formData.instagramHandle, 'instagram');
    if (igCheck.error) newErrors.instagramHandle = igCheck.error;
    const videoCheck = checkVideoLink(formData.featuredVideoUrl);
    if (videoCheck.error) newErrors.featuredVideoUrl = videoCheck.error;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!initialData?.id || !sessionToken) {
      toast.error('Unable to update profile. Please try again.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Handles go out only when changed here. The server writes any handle key
      // it gets, and '' clears it, so an old copy must not ride along.
      const xNow = normalizeSocialHandle(formData.xHandle, 'x').handle || '';
      const igNow = normalizeSocialHandle(formData.instagramHandle, 'instagram').handle || '';
      const videoNow = checkVideoLink(formData.featuredVideoUrl).url ?? '';
      const videoChanged = formData.featuredVideoUrl.trim() !== openedVideo.current.trim();
      const payload = {
        ...(videoChanged ? { featuredVideoUrl: videoNow } : {}),
        ...(xNow !== openedHandles.current.x ? { xHandle: xNow } : {}),
        ...(igNow !== openedHandles.current.instagram ? { instagramHandle: igNow } : {}),
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: cleanPhoneNumber(formData.phone),
        dateOfBirth: formData.dateOfBirth,
        graduationYear: parseInt(formData.graduationYear),
        city: formData.city,
        state: formData.state,
        ncaaId: formData.ncaaId || undefined,
      };

      const result = await athleteAPI.updateProfile(sessionToken, initialData.id, payload);

      if (result.profile) {
        toast.success('Personal info saved.');
        onUpdate(result.profile);
        // A new name can change the athlete login's name. Refresh it for the header.
        const nameChanged =
          formData.firstName !== (initialData.first_name || '') ||
          formData.lastName !== (initialData.last_name || '');
        if (nameChanged && user?.user_type === 'athlete') {
          userAPI.getProfile(sessionToken).then(setUser).catch(() => {});
        }
        (onSaved || onClose)();
      } else {
        throw new Error('Update failed');
      }
    } catch (error: any) {
      // The server sends the same video messages as utils/youtube.ts.
      if (error?.message === VIDEO_ERROR_HUDL || error?.message === VIDEO_ERROR_OTHER) {
        setErrors(prev => ({ ...prev, featuredVideoUrl: error.message }));
        return;
      }
      toast.error(error.message || 'Failed to update personal information');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setHasChanges(true);
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      inline={embedded}
      isDirty={hasChanges}
      labelledById="personal-title"
      contentStyle={{
        background: 'white',
        borderRadius: '12px',
        maxWidth: isMobile ? 'calc(100vw - 32px)' : '600px',
        width: isMobile ? 'calc(100vw - 32px)' : '100%',
        maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'hidden',
        fontFamily: 'Manrope, sans-serif',
      }}
    >
        {/* Header */}
        <div style={{
          padding: isMobile ? '16px' : '24px 28px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h2 id="personal-title" style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: '600',
            color: '#1F2937'
          }}>
            Edit Personal Information
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              transition: 'background 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#F3F4F6'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <X size={22} color="#6B7280" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: isMobile ? '20px 16px' : '28px'
          }}>
            {/* Every box on this sheet used to set `outline: none` inline and
                redraw its own focus border in onFocus/onBlur. The inline
                declaration outranks the global :focus-visible ring whatever its
                specificity, and the handlers only ever painted a border — so a
                keyboard user tabbing through the form saw nothing move. The
                primitives carry focus, invalid and disabled from globals.css,
                and the two selects lost the hand-drawn SVG chevron with the
                wrapper that held it. */}
            {/* First Name & Last Name Row */}
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '16px' : '20px', marginBottom: '20px' }}>
              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }} htmlFor="personal-first-name">
                  Athlete First Name *
                </label>
                <TextInput
                  id="personal-first-name"
                  invalid={!!errors.firstName}
                  aria-describedby={errors.firstName ? 'personal-first-name-error' : undefined}
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  placeholder="First Name"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: `1px solid ${errors.firstName ? '#EF4444' : '#D1D5DB'}`,
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'Manrope, sans-serif',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                />
                {errors.firstName && (
                  <span id="personal-first-name-error" role="alert" style={{ fontSize: '13px', color: '#EF4444', marginTop: '6px', display: 'block' }}>
                    {errors.firstName}
                  </span>
                )}
              </div>

              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }} htmlFor="personal-last-name">
                  Athlete Last Name *
                </label>
                <TextInput
                  id="personal-last-name"
                  invalid={!!errors.lastName}
                  aria-describedby={errors.lastName ? 'personal-last-name-error' : undefined}
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  placeholder="Last Name"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: `1px solid ${errors.lastName ? '#EF4444' : '#D1D5DB'}`,
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'Manrope, sans-serif',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                />
                {errors.lastName && (
                  <span id="personal-last-name-error" role="alert" style={{ fontSize: '13px', color: '#EF4444', marginTop: '6px', display: 'block' }}>
                    {errors.lastName}
                  </span>
                )}
              </div>
            </div>

            {/* Email */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }} htmlFor="personal-email">
                Athlete Email *
              </label>
              <TextInput
                id="personal-email"
                invalid={!!errors.email}
                aria-describedby={errors.email ? 'personal-email-error' : undefined}
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="Email"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: `1px solid ${errors.email ? '#EF4444' : '#D1D5DB'}`,
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontFamily: 'Manrope, sans-serif',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box'
                }}
              />
              {errors.email && (
                <span id="personal-email-error" role="alert" style={{ fontSize: '13px', color: '#EF4444', marginTop: '6px', display: 'block' }}>
                  {errors.email}
                </span>
              )}
            </div>

            {/* Phone */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }} htmlFor="personal-phone">
                Athlete Phone
              </label>
              <TextInput
                id="personal-phone"
                invalid={!!errors.phone}
                aria-describedby={errors.phone ? 'personal-phone-error' : undefined}
                type="tel"
                value={formatPhoneNumber(formData.phone)}
                onChange={(e) => {
                  const cleaned = cleanPhoneNumber(e.target.value);
                  if (cleaned.length <= 10) {
                    handleChange('phone', cleaned);
                  }
                }}
                placeholder="(555) 555-5555"
                maxLength={14}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: `1px solid ${errors.phone ? '#EF4444' : '#D1D5DB'}`,
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontFamily: 'Manrope, sans-serif',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box'
                }}
              />
              {errors.phone && (
                <span id="personal-phone-error" role="alert" style={{ fontSize: '13px', color: '#EF4444', marginTop: '6px', display: 'block' }}>
                  {errors.phone}
                </span>
              )}
            </div>

            {/* Date of Birth */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }} htmlFor="personal-date-of-birth">
                Athlete Date of Birth *
              </label>
              <TextInput
                id="personal-date-of-birth"
                invalid={!!errors.dateOfBirth}
                aria-describedby={errors.dateOfBirth ? 'personal-date-of-birth-error' : undefined}
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                min="1940-01-01"
                style={{
                  width: '100%',
                  maxWidth: '100%',
                  padding: '12px 14px',
                  border: `1px solid ${errors.dateOfBirth ? '#EF4444' : '#D1D5DB'}`,
                  borderRadius: '8px',
                  fontSize: isMobile ? '16px' : '15px',
                  fontFamily: 'Manrope, sans-serif',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  color: '#1F2937',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  appearance: 'none'
                }}
              />
              {errors.dateOfBirth && (
                <span id="personal-date-of-birth-error" role="alert" style={{ fontSize: '13px', color: '#EF4444', marginTop: '6px', display: 'block' }}>
                  {errors.dateOfBirth}
                </span>
              )}
            </div>

            {/* Graduation Year */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }} htmlFor="personal-graduation-year">
                Graduation Year *
              </label>
              <div style={{ position: 'relative' }}>
                <Select
                  id="personal-graduation-year"
                  invalid={!!errors.graduationYear}
                  aria-describedby={errors.graduationYear ? 'personal-graduation-year-error' : undefined}
                  value={formData.graduationYear}
                  onChange={(e) => handleChange('graduationYear', e.target.value)}
                  style={{
                    padding: '12px 14px',
                    paddingRight: '40px',
                    border: `1px solid ${errors.graduationYear ? '#EF4444' : '#D1D5DB'}`,
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'Manrope, sans-serif',
                    transition: 'all 0.2s ease',
                    color: '#1F2937',
                  }}
                >
                  <option value="" disabled>Select Year</option>
                  {generateGraduationYears().map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Select>
              </div>
              {errors.graduationYear && (
                <span id="personal-graduation-year-error" role="alert" style={{ fontSize: '13px', color: '#EF4444', marginTop: '6px', display: 'block' }}>
                  {errors.graduationYear}
                </span>
              )}
            </div>

            {/* NCAA ID (Optional) */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }} htmlFor="personal-ncaa-id">
                Athlete NCAA ID# (Optional)
              </label>
              <TextInput
                id="personal-ncaa-id"
                invalid={!!errors.ncaaId}
                aria-describedby={errors.ncaaId ? 'personal-ncaa-id-error' : undefined}
                type="text"
                value={formData.ncaaId}
                onChange={(e) => handleChange('ncaaId', e.target.value)}
                placeholder="Enter NCAA ID"
                maxLength={20}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontFamily: 'Manrope, sans-serif',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Social handles (Optional) — APS-255.

                Placed after the NCAA ID and built on the same field pattern as
                every box above it, hex values included: this modal is styled
                end to end with literals, and a token-coloured border on two
                fields in a column of nine reads as a rendering bug rather than
                as a design system. The tokens belong to a sweep of the whole
                modal, not to two new inputs inside it.

                The hint is the whole point of the field: paste the link, we
                will take it from there. */}
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '16px' : '20px', marginBottom: '20px' }}>
              <div style={{ flex: 1 }}>
                <label htmlFor="athlete-x-handle" style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Athlete X Handle (Optional)
                </label>
                <TextInput
                  id="athlete-x-handle"
                  invalid={!!errors.xHandle}
                  type="text"
                  value={formData.xHandle}
                  onChange={(e) => handleChange('xHandle', e.target.value)}
                  placeholder="@handle or x.com link"
                  maxLength={120}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-invalid={!!errors.xHandle}
                  aria-describedby={errors.xHandle ? 'athlete-x-handle-error' : 'athlete-social-hint'}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: `1px solid ${errors.xHandle ? '#EF4444' : '#D1D5DB'}`,
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'Manrope, sans-serif',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                />
                {errors.xHandle && (
                  <span id="athlete-x-handle-error" role="alert" style={{ fontSize: '13px', color: '#EF4444', marginTop: '6px', display: 'block' }}>
                    {errors.xHandle}
                  </span>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <label htmlFor="athlete-instagram-handle" style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Athlete Instagram Handle (Optional)
                </label>
                <TextInput
                  id="athlete-instagram-handle"
                  invalid={!!errors.instagramHandle}
                  type="text"
                  value={formData.instagramHandle}
                  onChange={(e) => handleChange('instagramHandle', e.target.value)}
                  placeholder="@handle or instagram.com link"
                  maxLength={120}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-invalid={!!errors.instagramHandle}
                  aria-describedby={errors.instagramHandle ? 'athlete-instagram-handle-error' : 'athlete-social-hint'}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: `1px solid ${errors.instagramHandle ? '#EF4444' : '#D1D5DB'}`,
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'Manrope, sans-serif',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                />
                {errors.instagramHandle && (
                  <span id="athlete-instagram-handle-error" role="alert" style={{ fontSize: '13px', color: '#EF4444', marginTop: '6px', display: 'block' }}>
                    {errors.instagramHandle}
                  </span>
                )}
              </div>
            </div>
            <p id="athlete-social-hint" style={{
              margin: '-8px 0 20px',
              fontSize: '13px',
              color: '#6B7280',
              fontFamily: 'Manrope, sans-serif',
              lineHeight: 1.5
            }}>
              Paste the profile link or type the handle. College coaches see these
              on your profile only while your privacy settings share your social links.
            </p>

            {/* APS-259. The highlight video coaches play on APS. */}
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="athlete-featured-video" style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Highlight video (YouTube link)
              </label>
              <TextInput
                id="athlete-featured-video"
                invalid={!!errors.featuredVideoUrl}
                type="url"
                inputMode="url"
                value={formData.featuredVideoUrl}
                onChange={(e) => handleChange('featuredVideoUrl', e.target.value)}
                placeholder="https://youtu.be/..."
                maxLength={500}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                aria-describedby={errors.featuredVideoUrl ? 'athlete-featured-video-error' : 'athlete-featured-video-hint'}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: `1px solid ${errors.featuredVideoUrl ? '#EF4444' : '#D1D5DB'}`,
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontFamily: 'Manrope, sans-serif',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box'
                }}
              />
              {errors.featuredVideoUrl ? (
                <span id="athlete-featured-video-error" role="alert" style={{ fontSize: '13px', color: '#EF4444', marginTop: '6px', display: 'block' }}>
                  {errors.featuredVideoUrl}
                </span>
              ) : (
                <span id="athlete-featured-video-hint" style={{ fontSize: '13px', color: '#6B7280', marginTop: '6px', display: 'block', lineHeight: 1.5 }}>
                  Coaches on APS filter for video first.
                </span>
              )}
            </div>

            {/* City & State Row */}
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '16px' : '20px' }}>
              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }} htmlFor="personal-city">
                  Athlete City *
                </label>
                <TextInput
                  id="personal-city"
                  invalid={!!errors.city}
                  aria-describedby={errors.city ? 'personal-city-error' : undefined}
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="City"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: `1px solid ${errors.city ? '#EF4444' : '#D1D5DB'}`,
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'Manrope, sans-serif',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                />
                {errors.city && (
                  <span id="personal-city-error" role="alert" style={{ fontSize: '13px', color: '#EF4444', marginTop: '6px', display: 'block' }}>
                    {errors.city}
                  </span>
                )}
              </div>

              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }} htmlFor="personal-state">
                  State *
                </label>
                <div style={{ position: 'relative' }}>
                  <Select
                    id="personal-state"
                    invalid={!!errors.state}
                    aria-describedby={errors.state ? 'personal-state-error' : undefined}
                    value={formData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    style={{
                      padding: '12px 14px',
                      paddingRight: '40px',
                      border: `1px solid ${errors.state ? '#EF4444' : '#D1D5DB'}`,
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontFamily: 'Manrope, sans-serif',
                      transition: 'all 0.2s ease',
                      color: '#1F2937',
                    }}
                  >
                    <option value="" disabled>Select State</option>
                    {US_STATES.map((state) => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </Select>
                </div>
                {errors.state && (
                  <span id="personal-state-error" role="alert" style={{ fontSize: '13px', color: '#EF4444', marginTop: '6px', display: 'block' }}>
                    {errors.state}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{
            padding: '20px 28px',
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                padding: '12px 24px',
                background: 'white',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                color: '#374151',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontFamily: 'Manrope, sans-serif',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) e.currentTarget.style.background = '#F9FAFB';
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) e.currentTarget.style.background = 'white';
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: '12px 28px',
                background: isSubmitting ? '#9CA3AF' : '#F9550D',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                color: 'white',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontFamily: 'Manrope, sans-serif',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) e.currentTarget.style.background = '#E04A0A';
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) e.currentTarget.style.background = '#F9550D';
              }}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
    </Modal>
  );
};

export default PersonalInfoEditModal;
